import type { StaffPaymentMethodType } from "services/api/company/staff.service";

export type PaymentMethodValidationError =
  | "empty"
  | "bankAccountFormat"
  | "internationalBankAccountCharacters"
  | "internationalBankAccountCountryCode"
  | "internationalBankAccountLength";

export type PaymentMethodValidationResult =
  | { type: "success"; data: string }
  | { type: "failure"; error: PaymentMethodValidationError };

export type PaymentMethodValidationTranslationKey =
  | "Staff.PaymentMethodValueRequired"
  | "Staff.PaymentMethodBankAccountInvalid"
  | "Staff.PaymentMethodInternationalBankAccountCharactersInvalid"
  | "Staff.PaymentMethodInternationalBankAccountCountryCodeInvalid"
  | "Staff.PaymentMethodInternationalBankAccountLengthInvalid";

export type PaymentMethodHintTranslationKey =
  | "Staff.PaymentMethodValueHintBankAccount"
  | "Staff.PaymentMethodValueHintInternationalBankAccount";

export type PaymentMethodFallbackErrorTranslationKey =
  | "Staff.PaymentMethodValueInvalidBankAccount"
  | "Staff.PaymentMethodInternationalBankAccountInvalid";

export interface PaymentMethodOption {
  label: string;
  value: StaffPaymentMethodType;
}

const BANK_ACCOUNT_REGEX = /^\d{11,15}$/;
const IBAN_ALLOWED_CHARACTERS_REGEX = /^[A-Z0-9]+$/;
const IBAN_COUNTRY_CODE_REGEX = /^[A-Z]{2}/;
const IBAN_MIN_LENGTH = 15;
const IBAN_MAX_LENGTH = 34;
const IBAN_CHECKSUM_FAILURE_MESSAGE =
  "The IBAN checksum failed. Please double-check for typos.";

function assertUnreachable(value: never): never {
  throw new Error(`Unhandled payment method validation error: ${value}`);
}

export const PAYMENT_METHOD_OPTIONS: PaymentMethodOption[] = [
  {
    value: "BankAccount",
    label: "Staff.PaymentMethodBankAccount",
  },
  {
    value: "InternationalBankAccount",
    label: "Staff.PaymentMethodInternationalBankAccount",
  },
];

export const normalizePaymentMethodValue = (value: string): string => {
  return value.trim();
};

export const validatePaymentMethodValue = (
  type: StaffPaymentMethodType,
  value: string,
): PaymentMethodValidationResult => {
  if (value.trim().length === 0) {
    return { type: "failure", error: "empty" };
  }

  if (type === "BankAccount") {
    return BANK_ACCOUNT_REGEX.test(value)
      ? { type: "success", data: value }
      : { type: "failure", error: "bankAccountFormat" };
  }

  if (!IBAN_ALLOWED_CHARACTERS_REGEX.test(value)) {
    return { type: "failure", error: "internationalBankAccountCharacters" };
  }

  if (!IBAN_COUNTRY_CODE_REGEX.test(value)) {
    return { type: "failure", error: "internationalBankAccountCountryCode" };
  }

  if (value.length < IBAN_MIN_LENGTH || value.length > IBAN_MAX_LENGTH) {
    return { type: "failure", error: "internationalBankAccountLength" };
  }

  return { type: "success", data: value };
};

export function isIbanChecksumFailure(message: string | null): boolean {
  return message === IBAN_CHECKSUM_FAILURE_MESSAGE;
}

export const isPaymentMethodValueValidOrEmpty = (
  type: StaffPaymentMethodType,
  value: string,
): boolean => {
  if (value.trim().length === 0) return true;
  return validatePaymentMethodValue(type, value).type === "success";
};

export const toPaymentMethodValidationTranslationKey = (
  error: PaymentMethodValidationError,
): PaymentMethodValidationTranslationKey => {
  switch (error) {
    case "empty":
      return "Staff.PaymentMethodValueRequired";
    case "bankAccountFormat":
      return "Staff.PaymentMethodBankAccountInvalid";
    case "internationalBankAccountCharacters":
      return "Staff.PaymentMethodInternationalBankAccountCharactersInvalid";
    case "internationalBankAccountCountryCode":
      return "Staff.PaymentMethodInternationalBankAccountCountryCodeInvalid";
    case "internationalBankAccountLength":
      return "Staff.PaymentMethodInternationalBankAccountLengthInvalid";
    default:
      return assertUnreachable(error);
  }
};

export const toPaymentMethodHintTranslationKey = (
  type: StaffPaymentMethodType,
): PaymentMethodHintTranslationKey => {
  if (type === "InternationalBankAccount") {
    return "Staff.PaymentMethodValueHintInternationalBankAccount";
  }

  return "Staff.PaymentMethodValueHintBankAccount";
};

export const toPaymentMethodFallbackErrorTranslationKey = (
  type: StaffPaymentMethodType,
): PaymentMethodFallbackErrorTranslationKey => {
  if (type === "InternationalBankAccount") {
    return "Staff.PaymentMethodInternationalBankAccountInvalid";
  }

  return "Staff.PaymentMethodValueInvalidBankAccount";
};
